/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { AxisBottom } from "@visx/axis";
import { LinearGradient } from "@visx/gradient";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import dayjs from "dayjs";
import { theme } from "@nordcloud/gnui";
import { BrickLoader } from "~/components/Loaders";
import { LoaderWrap } from "~/components/Utils";
import { isEmpty } from "~/tools";
import { DataItem } from "../types";
import { getXScale, getYScale, LeftAxis, dateFormatter } from "../utils";
import { ChartPlaceholder } from "./ChartPlaceholder";

const VERTICAL_MARGIN = 40;
const FONT_SIZE = 9;

type Props<T> = {
  width: number;
  height: number;
  data: T[];
  forecast?: number;
  yTicks?: number;
  threshold?: number;
  maxYValue?: number;
  children: React.ReactNode;
  loading?: boolean;
  numTicks?: number;
  offset?: number;
  xTickFormat?: (value: number) => string;
  yTickFormat?: (value: number) => string;
};

export function ChartContainer<T extends DataItem>({
  width,
  height,
  data = [],
  yTicks = 5,
  children,
  loading = true,
  numTicks = 12,
  maxYValue = 100,
  offset = 30,
  yTickFormat = (n) => `${n}%`,
  xTickFormat = dateFormatter,
}: Props<T>) {
  const xMax = width - offset;
  const yMax = height - VERTICAL_MARGIN;
  const chartData = new Array(numTicks).fill(0);
  const xScale = getXScale(xMax, chartData);
  const yScale = getYScale(yMax, maxYValue);

  data.forEach((item) =>
    chartData.splice(
      dayjs(item?.date).month(),
      1,
      Number(item?.value) * 100 || 0
    )
  );

  return (
    <LoaderWrap
      loading={loading}
      Component={BrickLoader}
      viewBox="0 0 10 4"
      backgroundColor={theme.color.background.ui01}
    >
      <div
        css={{
          position: "relative",
        }}
      >
        <ChartPlaceholder show={isEmpty(data)} width={width} height={height} />
        <svg width={width} height={height}>
          <LinearGradient
            to={theme.color.support.grey}
            from={theme.color.support.grey}
            id="barFill"
          />
          <GridRows
            scale={yScale}
            width={xMax}
            top={VERTICAL_MARGIN / 2}
            left={offset}
            strokeDasharray="1,3"
            stroke={theme.color.border.focus}
            strokeOpacity={1}
            pointerEvents="none"
            numTicks={yTicks}
          />
          <Group top={VERTICAL_MARGIN / 2} left={offset}>
            <LeftAxis
              yScale={yScale}
              yTicks={yTicks}
              fontSize={FONT_SIZE}
              tickFormat={yTickFormat}
            />
            <AxisBottom
              top={yMax}
              scale={xScale}
              stroke={theme.color.border.focus}
              tickStroke={theme.color.border.focus}
              numTicks={numTicks}
              tickFormat={xTickFormat}
              tickLabelProps={() => ({
                fill: theme.color.text.text02,
                fontSize: FONT_SIZE,
                textAnchor: "middle",
              })}
            />
            {children}
          </Group>
        </svg>
      </div>
    </LoaderWrap>
  );
}
